import { Link } from 'react-router';

import {
  PRIVACY_CONTACT_EMAIL,
  PRIVACY_EFFECTIVE_DATE,
  privacyPolicy,
  type PolicyBlock,
  type PolicySection,
} from './privacyPolicy';

import './legal.css';

/**
 * Formats the effective date for display, falling back to the raw ISO value if the runtime cannot
 * format it.
 *
 * @param iso - The ISO date string.
 * @returns A human-readable date.
 */
function formatEffectiveDate(iso: string): string {
  const parsed = new Date(`${iso}T00:00:00Z`);

  if (Number.isNaN(parsed.getTime())) {
    return iso;
  }

  return parsed.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: 'UTC',
  });
}

/**
 * Renders one content block of a policy section.
 *
 * @param props - The component props.
 * @param props.block - The block to render.
 * @returns The block element.
 */
function PolicyBlockView({ block }: { block: PolicyBlock }): React.JSX.Element {
  if (block.kind === 'text') {
    return <p className="gm-legal__text">{block.text}</p>;
  }

  if (block.kind === 'list') {
    return (
      <ul className="gm-legal__list">
        {block.items.map((item) => (
          <li key={item}>{item}</li>
        ))}
      </ul>
    );
  }

  return (
    <div className="gm-legal__table-wrap">
      <table className="gm-legal__table">
        <thead>
          <tr>
            <th scope="col">{block.head[0]}</th>
            <th scope="col">{block.head[1]}</th>
          </tr>
        </thead>
        <tbody>
          {block.rows.map(([label, detail]) => (
            <tr key={label}>
              <th scope="row">{label}</th>
              <td>{detail}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Renders one section of the policy.
 *
 * @param props - The component props.
 * @param props.section - The section to render.
 * @returns The section element.
 */
function PolicySectionView({ section }: { section: PolicySection }): React.JSX.Element {
  return (
    <section className="gm-legal__section" aria-labelledby={`${section.id}-heading`}>
      <h2 id={`${section.id}-heading`} className="gm-legal__section-title">
        {section.title}
      </h2>

      {section.blocks.map((block, index) => (
        // Blocks are a fixed, ordered list authored by hand, so the index is a stable key.
        <PolicyBlockView key={`${section.id}-${String(index)}`} block={block} />
      ))}
    </section>
  );
}

/**
 * The privacy policy page.
 *
 * Deliberately a public route: a visitor must be able to read how their data would be handled
 * before deciding to create an account, and advertising platforms require a reachable policy.
 *
 * @returns The privacy page element.
 */
export function PrivacyPage(): React.JSX.Element {
  return (
    <div className="gm-legal">
      <a className="gm-skip-link" href="#main">
        Skip to content
      </a>

      <header className="gm-legal__header">
        <Link to="/" className="gm-legal__brand">
          Grease<span className="gm-legal__brand-mark">map</span>
        </Link>
      </header>

      <main id="main" className="gm-legal__main">
        <h1 className="gm-legal__title">Privacy policy</h1>

        <p className="gm-legal__meta">
          Effective {formatEffectiveDate(PRIVACY_EFFECTIVE_DATE)} ·{' '}
          <a className="gm-legal__link" href={`mailto:${PRIVACY_CONTACT_EMAIL}`}>
            {PRIVACY_CONTACT_EMAIL}
          </a>
        </p>

        <nav className="gm-legal__toc" aria-label="Sections">
          <ol>
            {privacyPolicy.map((section) => (
              <li key={section.id}>
                <a className="gm-legal__link" href={`#${section.id}-heading`}>
                  {section.title}
                </a>
              </li>
            ))}
          </ol>
        </nav>

        {privacyPolicy.map((section) => (
          <PolicySectionView key={section.id} section={section} />
        ))}
      </main>

      <footer className="gm-legal__footer">
        <Link className="gm-legal__link" to="/">
          Back to Greasemap
        </Link>
      </footer>
    </div>
  );
}
